import { CameraIcon, Plus, Trash2 } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import Cropper, { type Area } from 'react-easy-crop'

import type { GetMeResponse } from '@snootsvet/shared/portal'

import { Button } from '@/customer/components/ui/button'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/customer/components/ui/dialog'
import { useUpdatePet } from '@/customer/hooks/use-update-pet'
import { useUploadFile } from '@/customer/hooks/use-upload-file'
import { cropImageToJpeg } from '@/customer/lib/images'

export function PetPhotoDialog({
  pet,
  onClose,
}: {
  pet: GetMeResponse['patients'][number]
  onClose: () => void
}) {
  const { mutateAsync: uploadFile } = useUploadFile()
  const { mutateAsync: updatePet } = useUpdatePet(pet.id)
  const inputRef = useRef<HTMLInputElement>(null)
  const [file, setFile] = useState<File | null>(null)
  const [removed, setRemoved] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [crop, setCrop] = useState({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)
  const [area, setArea] = useState<Area | null>(null)
  const ready = area !== null
  const canSave = !saving && (file ? ready : removed)
  const previewUrl = useMemo(() => (file ? URL.createObjectURL(file) : null), [file])
  useEffect(
    () => () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl)
    },
    [previewUrl]
  )
  const shownUrl = previewUrl ?? (removed ? null : pet.photoUrl)

  function resetCrop() {
    setCrop({ x: 0, y: 0 })
    setZoom(1)
    setArea(null)
  }

  async function save() {
    if (!canSave) return
    setSaving(true)
    setError(null)
    try {
      if (file && area) {
        const photo = await cropImageToJpeg(file, area, 1024)
        const uploaded = await uploadFile(photo)
        await updatePet({
          photo: { ...uploaded, contentType: 'image/jpeg', sizeBytes: photo.size },
        })
      } else {
        await updatePet({ photo: null })
      }
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : `Could not save ${pet.name}’s photo`)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !saving) onClose()
      }}
      disablePointerDismissal
    >
      <DialogContent
        showCloseButton={!saving}
        className="sm:max-w-md"
        style={{ animation: 'none' }}
      >
        <DialogHeader tone={pet.sex === 'male' ? 'baby-blue' : 'pink'}>
          <DialogTitle>{pet.name}’s photo</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <fieldset disabled={saving} className="flex flex-col items-center gap-4">
            <fieldset
              aria-label="Pet photo"
              inert={saving}
              className="relative aspect-square w-full max-w-64 touch-none overflow-hidden rounded-2xl bg-muted focus-visible:outline-2 focus-visible:outline-offset-4"
            >
              {previewUrl ? (
                <Cropper
                  key={previewUrl}
                  image={previewUrl}
                  crop={crop}
                  zoom={zoom}
                  aspect={1}
                  minZoom={1}
                  maxZoom={3}
                  objectFit="cover"
                  zoomSpeed={0.5}
                  showGrid={false}
                  onCropChange={(next) => {
                    if (!saving) setCrop(next)
                  }}
                  onZoomChange={(next) => {
                    if (!saving) setZoom(next)
                  }}
                  onCropAreaChange={(_, pixels) => setArea(pixels)}
                  onTouchRequest={() => !saving}
                  onWheelRequest={() => !saving}
                  cropperProps={{
                    'aria-label': 'Photo crop. Use arrow keys to move the photo.',
                    tabIndex: saving ? -1 : 0,
                  }}
                  mediaProps={{
                    alt: pet.name,
                    onError: () => {
                      resetCrop()
                      setError(
                        'This photo could not be opened. Please choose a JPEG, PNG or WebP image.'
                      )
                    },
                  }}
                  style={{ cropAreaStyle: { border: 0, boxShadow: 'none' } }}
                  classes={{
                    cropAreaClassName:
                      'focus-visible:outline-2 focus-visible:outline-offset-[-4px]',
                  }}
                />
              ) : shownUrl ? (
                <img src={shownUrl} alt={pet.name} className="size-full object-cover" />
              ) : (
                <span className="flex size-full flex-col items-center justify-center gap-2 text-body-sm text-muted-foreground">
                  <CameraIcon className="size-8" aria-hidden />
                  No photo yet
                </span>
              )}
            </fieldset>
            {file && ready && (
              <div className="flex w-full flex-col items-center gap-3">
                <p className="text-body-sm">Drag to position. Pinch or scroll to zoom.</p>
                <label className="flex flex-col items-center gap-1 text-button">
                  Zoom
                  <input
                    type="range"
                    min={1}
                    max={3}
                    step={0.01}
                    value={zoom}
                    onChange={(event) => setZoom(Number(event.target.value))}
                  />
                </label>
              </div>
            )}
            <div className="flex flex-col items-center">
              <Button type="button" variant="ghost" onClick={() => inputRef.current?.click()}>
                <Plus className="size-4" aria-hidden />
                {shownUrl ? 'Change photo' : 'Upload photo'}
              </Button>
              {shownUrl && (
                <Button
                  type="button"
                  variant="ghost-destructive"
                  onClick={() => {
                    setFile(null)
                    setRemoved(true)
                    resetCrop()
                    setError(null)
                  }}
                >
                  <Trash2 className="size-4" aria-hidden />
                  Remove photo
                </Button>
              )}
            </div>
            <input
              ref={inputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              aria-label="Choose pet photo"
              className="hidden"
              onChange={(event) => {
                const picked = event.target.files?.[0]
                event.target.value = ''
                if (!picked) return
                if (!['image/jpeg', 'image/png', 'image/webp'].includes(picked.type)) {
                  setError('Please choose a JPEG, PNG or WebP image.')
                  return
                }
                if (picked.size > 20 * 1024 * 1024) {
                  setError('Please choose a photo smaller than 20 MB.')
                  return
                }
                setFile(picked)
                setRemoved(false)
                resetCrop()
                setError(null)
              }}
            />
          </fieldset>
          {error && (
            <p role="alert" className="text-destructive">
              {error}
            </p>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" disabled={saving} onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={!canSave} onClick={save}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
